import { Check, Loader2, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  getApiErrorMessage,
  type Room,
  useApplyWindowMapping,
  useRescanWindows,
  useSetWindowPosition,
  useWindowsStatus,
  type WindowCover,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { liveRooms } from "@/lib/rooms";
import { spellCount, spellCountLower } from "@/lib/spell-count";
import { cn } from "@/lib/utils";

import {
  AppBar,
  formatStatusDetail,
  HelpHint,
  MaterialsNeeded,
  PhaseDots,
  PillButton,
  SkipFailedStep,
  STUCK_HELP_MS,
  StuckHelp,
} from "./components";
import { PRODUCT_NAMES } from "./product-names";
import {
  describeBlindsSetAside,
  describeDiscovery,
  describeMappingProgress,
  describeScan,
  labelForCover,
  mappingLoopDone,
} from "./window-inventory";

/**
 * Phase 6 · Windows — pair the household's VELUX io-homecontrol windows.
 *
 * Three workstreams: copy the household's existing remotes into the VELUX
 * Touch, hand the keys from the VELUX Touch to the VELUX Window Connector, then map
 * each actuator Home Assistant exposes to a room.
 *
 * Which Touch depends on the remote-inventory branch (`remote_check`, VLX-99):
 * a home that already uses a Touch routes everything through THEIR VELUX Touch —
 * keys can never be copied off it (VLX-72) — and a home whose own Touch
 * already moves every window skips the copy chapter entirely, entering at
 * `klg_intro`. The kit's new Touch only appears on the no-Touch path.
 *
 * Only part three can be verified by the app. Everything before it is button
 * work on VELUX hardware we cannot see, so those screens are confirm-only. That
 * shapes the whole compartment: where the app cannot check, it must at least be
 * unambiguous about what to press, and it must own the two things the resident
 * cannot track alone. Those are the VELUX Touch's ten-minute copy window (the clock
 * below) and the recovery when it closes.
 *
 * One app screen per Touch screen. The resident navigates by picture far more
 * than by text, so a screen showing two different Touch states makes them work
 * out which one they are on. `copy_start` and `copy_match` look nearly
 * identical in the app for that reason: they mirror two consecutive Touch
 * screens that differ only in their button row.
 *
 * `klg_transfer` is the deliberate exception, holding a Touch press and a
 * connector press together. Splitting them costs more than the inconsistency,
 * because the VELUX Touch only offers the keys for about two minutes and the resident
 * would spend that window navigating an app.
 *
 * The copy runs one round per TYPE of remote, not one round per household and
 * not one per remote. Four identical wall remotes are a single round: pick the
 * type on `copy_match`, then press all four during that ten minutes. A house
 * with wall remotes and Touch remotes does the loop twice, which is what the
 * second CTA on `copy_active` is for. Confirmed with VELUX, and worth stating
 * because the carousel picture makes it look per-remote and the ten-minute
 * window makes it look per-household.
 *
 * Real wiring: the connector check polls Home Assistant for a loaded KLG 300,
 * discovery lists the actuators it actually exposes (with a rescan that reloads
 * the integration, since it enumerates actuators once at setup), and the
 * mapping loop nudges one window to 25% through HA, asks which moved, closes it
 * again, then renames each actuator into its room ("Kitchen cover") while
 * persisting the binding on the household.
 */

/** VELUX Windows' flat module block — brand Background Yellow. See the tint
 *  map in globals.css; the same colour marks this module on the guide. */
const MODULE_HERO = "bg-daylight-subtle";

/** Poll cadence while waiting for the connector / its actuators. */
const POLL_MS = 3000;

/** How far we open a window so it's visibly moving but never wide open. */
const NUDGE_POSITION = 25;

/** Beat before asking which window moved — actuators travel for a second. */
const OPEN_SETTLE_MS = 2500;

/**
 * Gap between closing and re-opening on "open it again", and how long a window
 * stays open when a mapped row is verified.
 *
 * Re-opening needs the close first: the window is already sitting at
 * NUDGE_POSITION, so a second command to the same position moves nothing, and
 * the movement is the entire point of asking again.
 */
const REOPEN_GAP_MS = 1500;
const VERIFY_HOLD_MS = 6000;

/**
 * How long the VELUX Touch stays open for copying, once the resident confirms the
 * checkmark. This is the VELUX Touch's own behaviour, not ours: it drops back to its
 * start screen when the time is up, taking the app's instructions out of sync
 * with the device. Showing the clock is the only way the resident can pace the
 * walk around the house, and `copy_expired` is what happens when they cannot.
 */
const COPY_WINDOW_MS = 10 * 60 * 1000;

/**
 * Reloads to attempt automatically at the discovery stage, and the wait
 * between them. The connector may still be registering the windows in the
 * seconds after the key transfer, so the first reload can be too early.
 */
const MAX_AUTO_RELOADS = 3;
const RELOAD_RETRY_MS = 12_000;

// "VELUX Touch" is the retail name kept on purpose (it's printed on the remote
// the user has to find). The connector's model number is deliberately never
// shown: users see it only as the VELUX Window Connector — singular, because it
// connects VELUX windows and nothing else, and the name should say so.
const TOUCH_REMOTE = PRODUCT_NAMES.touchRemote;
const CONNECTOR = PRODUCT_NAMES.windowConnector;

/**
 * Which Touch drives this install: the new one from the kit, or one the
 * household already owns (VLX-99, child of blocker VLX-72).
 *
 * Why this branch exists: a plain remote hands its window key to ONE Touch,
 * ever (confirmed by Simon, 17 Aug). A home that already uses a Touch cannot
 * copy those keys onto the kit's new Touch, so the whole first chapter
 * dead-ends there — it did in all three beta homes. When a Touch already
 * exists, everything must route through THEIR VELUX Touch, and the kit's stays in
 * its box (some kits will not even include one once the pre-ship hardware
 * inventory can tell us up front).
 */
type RemotePath = "kit_touch" | "own_touch";

/**
 * The household's own Touch model. The models pair differently (Francis,
 * 26 Aug 2026), so the own-Touch path asks which one and keys its
 * instructions off the answer.
 */
type OwnTouchModel = "KLR 100" | "KLR 200" | "KLR 300";

/** One illustrated instruction: what to tap, and the pad screen showing it. */
interface IllustratedStep {
  img: string | null;
  text: string;
}

/**
 * Per-model routes to the copy screen on the household's OWN Touch — the
 * standard screens describe the kit KLR 300's first-run wizard, which an
 * already-set-up Touch never shows.
 *
 * The KLR 300 and KLR 200 routes are from Francis's device walkthroughs
 * (28 Aug 2026); device labels are quoted as each Touch prints them. `null`
 * (KLR 100, unsupported and rerouted to the kit path before this screen)
 * falls back to the generic entry text below.
 */
const OWN_TOUCH_COPY_ENTRY: Record<OwnTouchModel, IllustratedStep[] | null> = {
  "KLR 100": null,
  // Shares its first three screens with the send-mode walk; the routes only
  // fork at the "New products" menu ("Add new products" to copy remotes in,
  // "Copy control pad" to send keys out). Confirmed by Francis, 28 Aug 2026.
  "KLR 200": [
    {
      img: "/brand/onboarding/KLR200_1_awaken_controller.png",
      text: "Tap the Home button to wake your VELUX Touch.",
    },
    {
      img: "/brand/onboarding/KLR200_2_tap_plus_button.png",
      text: "Tap the plus button in the top right corner.",
    },
    {
      img: "/brand/onboarding/KLR200_3_new_product.png",
      text: 'Tap "New product".',
    },
    {
      img: "/brand/onboarding/KLR200_4_add_new_product.png",
      text: 'Tap "Add new products". The VELUX Touch offers to copy from another remote.',
    },
  ],
  "KLR 300": [
    {
      img: "/brand/onboarding/KLR300_1_tap_gear_icon.png",
      text: "On the home screen, tap the gear in the top right corner.",
    },
    {
      img: "/brand/onboarding/KLR300_2_next_more_options.png",
      text: "Tap the next arrow in the bottom right corner for more options.",
    },
    {
      img: "/brand/onboarding/KLR300_3_add_products.png",
      text: 'Tap "Add Products". The VELUX Touch offers to copy from another remote.',
    },
  ],
};

/** The ghost link into a rescue, worded identically at every entry. */
const RESCUE_LINK_LABEL = `The ${TOUCH_REMOTE} isn't showing this`;

/**
 * Wake-rescue checks (VLX-81), in dead-device order: a blank brand-new
 * VELUX Touch is a power problem, so the tab and batteries come first and
 * remotes are never mentioned.
 */
const WAKE_RESCUE_STEPS = [
  "Pull the battery tab all the way out. A half-pulled tab keeps the batteries disconnected.",
  "Give it a moment. The screen takes a few seconds after the batteries connect.",
  "Still blank? Open the battery lid, take the batteries out, and put them back in.",
  "If nothing wakes it, try fresh batteries.",
];

/**
 * Pairing-rescue checks (VLX-81), in evidence order. Worded to be true from
 * both entries: on copy_start and copy_match no remote has been pressed yet,
 * so the remote advice is conditional rather than presumed (Nico's review).
 */
const PAIRING_RESCUE_STEPS = [
  `Give it a moment. The ${TOUCH_REMOTE}'s screen can lag a few seconds.`,
  `The ${TOUCH_REMOTE} returns to its home screen when it idles. If it did, start the copy again from the beginning. Keys that already copied are kept.`,
  `If a remote you pressed didn't register, bring it within arm's length of the ${TOUCH_REMOTE} and check its batteries.`,
];

/** What the copy screen says while a model's real steps are pending. */
const OWN_TOUCH_COPY_FALLBACK: IllustratedStep[] = [
  {
    img: null,
    text: `On your ${TOUCH_REMOTE}, open the menu where it copies from another remote.`,
  },
  { img: null, text: "Tap the forward arrow when it offers to copy." },
];

/**
 * The own-Touch model picker's cards. Only the KLR 300 has a render today;
 * the older models show a named placeholder until photos land (asset request
 * with Nico). Recognition beats recall here: a resident knows the shape of
 * the remote on their wall, not its model number, so the picker leads with
 * images and offers the model plate on the back as the fallback check.
 */
const OWN_TOUCH_MODELS: {
  model: OwnTouchModel;
  img: string | null;
  caption: string;
  disabled: boolean;
}[] = [
  {
    model: "KLR 300",
    img: "/brand/onboarding/windows-touch-home.png",
    caption: "E-ink touchscreen.",
    disabled: false,
  },
  {
    model: "KLR 200",
    img: "/brand/onboarding/KLR200.png",
    caption: "Older touchscreen.",
    disabled: false,
  },
  {
    model: "KLR 100",
    img: "/brand/onboarding/KLR100.png",
    caption: "Buttons under a small screen. Not supported in this version yet.",
    disabled: true,
  },
];

/**
 * Touch UI imagery, per stage of the copy/transfer choreography.
 *
 * The kit set is the KLR 300 the box ships. The resident navigates by picture
 * far more than by text (see the module docstring), so the own-Touch path must
 * show THEIR model's screens: a KLR 200 owner squinting at a KLR 300 render is
 * matching against a device they don't have.
 */
type TouchImageKey = "home" | "copy" | "select" | "check" | "keys" | "count" | "sendkey";

const KIT_TOUCH_IMAGES: Record<TouchImageKey, string> = {
  home: "/brand/onboarding/windows-touch-home.png",
  copy: "/brand/onboarding/windows-touch-copy.png",
  select: "/brand/onboarding/windows-touch-select.png",
  check: "/brand/onboarding/windows-touch-check.png",
  keys: "/brand/onboarding/windows-touch-keys.png",
  count: "/brand/onboarding/windows-touch-count.png",
  sendkey: "/brand/onboarding/windows-touch-sendkey.png",
};

/**
 * Model-specific UI renders for the own-Touch path. The KLR 300's screens are
 * the kit set; the KLR 200's are pending (asset request alongside Nico's
 * instruction document). A missing entry falls back to the model's device
 * photo below rather than another model's UI state: a generic photo of the
 * right device beats a precise render of the wrong one.
 */
const OWN_TOUCH_IMAGES: Record<OwnTouchModel, Partial<Record<TouchImageKey, string>>> = {
  "KLR 300": KIT_TOUCH_IMAGES,
  "KLR 200": {},
  "KLR 100": {},
};

/** Each model's plain device photo — the fallback when a UI render is missing. */
const OWN_TOUCH_PHOTO: Record<OwnTouchModel, string> = {
  "KLR 300": "/brand/onboarding/windows-touch-home.png",
  "KLR 200": "/brand/onboarding/KLR200.png",
  "KLR 100": "/brand/onboarding/KLR100.png",
};

/**
 * The KLR 300's key transfer, from Francis's device test (3 Sept 2026).
 *
 * The button on the back is recessed and needs real, sustained pressure
 * through the pin. "SEND KEY" appears after four to six seconds and the press
 * must end the moment it does: pressing again (a second press, or one that
 * keeps going) flips the screen to "RECEIVE KEY", the wrong mode, which is
 * invisible unless you know to read the arrow. Esben's beta install went
 * exactly there. So the correct and the wrong screen are both pictured, and
 * the check on the arrow is a step of its own. Device text is transcribed in
 * the capitals the screen uses.
 */
const KLR300_KEY_TRANSFER_STEPS: IllustratedStep[] = [
  {
    img: "/brand/onboarding/key-button-to-press-on-KLR300.jpg",
    text: `Take the battery lid off the back of the ${TOUCH_REMOTE}. Leave the batteries in. Press the button with the key icon using the pin, firmly, and keep the pressure on.`,
  },
  {
    img: "/brand/onboarding/windows-touch-sendkey-correct.png",
    text: 'After four to six seconds the screen shows "SEND KEY", with the arrow pointing away from the key. Let go the moment it appears.',
  },
  {
    img: "/brand/onboarding/windows-touch-receivekey-incorrect.png",
    text: `Check the arrow. Pointing towards the key means "RECEIVE KEY", the wrong mode. That happens when the button is pressed twice.`,
  },
  {
    img: "/brand/onboarding/key-button-to-press-on-KLG.jpg",
    text: "On the connector, press the key button with the pin. Less than one second. A green light flashes slowly.",
  },
  {
    img: null,
    text: `When "SEND KEY" disappears from the ${TOUCH_REMOTE}, the keys are across.`,
  },
];

/**
 * The KLR 200's route into sending mode (Francis's device walkthrough,
 * 27 Aug 2026). The KLR 300 gets there with one held button, folded into
 * `klg_transfer` itself; the KLR 200 walks a five-screen menu path, so it
 * gets its own stage (`klg_send_mode`) with one illustrated step per pad
 * screen. Menu names are quoted exactly as the pad prints them. Everything
 * after sending mode — the connector's key press, the connector setup — is
 * identical for both models.
 */
const KLR200_SEND_MODE_STEPS: IllustratedStep[] = [
  {
    img: "/brand/onboarding/KLR200_1_awaken_controller.png",
    text: "Tap the Home button to wake your VELUX Touch.",
  },
  {
    img: "/brand/onboarding/KLR200_2_tap_plus_button.png",
    text: "Tap the plus button in the top right corner.",
  },
  {
    img: "/brand/onboarding/KLR200_3_new_product.png",
    text: 'Tap "New product".',
  },
  {
    img: "/brand/onboarding/KLR200_4_copy_control_pad.png",
    text: 'Tap "Copy control pad".',
  },
  {
    img: "/brand/onboarding/KLR200_5_tap_next_arrow.png",
    text: 'The "Send copy" screen explains what happens next. Tap the forward arrow.',
  },
];

/**
 * The remote-inventory cards (`remote_check`). Multi-select, because homes own
 * mixes — forcing one answer made the resident rank their own hardware, and
 * the ranking rule (a Touch outranks wall switches) is ours to apply, not
 * theirs to guess. Titles name the product family; the model numbers ride in
 * the subtitle for the resident who checks the back of the device.
 */
const REMOTE_KINDS = [
  {
    kind: "wall_switch",
    title: "Basic Wall Switch(es)",
    subtitle: "KLI 310/311/312/313",
    img: "/brand/onboarding/windows-remote.png",
    img2: null,
    disabled: false,
  },
  {
    kind: "velux_touch",
    title: "Multiple Product Switch",
    subtitle: "VELUX Touch KLR 100/200/300",
    // Both supported models pictured: the subtitle names three, and a card
    // showing only the KLR 300 left a KLR 200 owner unable to recognise
    // their own remote in it (Francis, 2 Sept 2026).
    img: "/brand/onboarding/windows-touch-home.png",
    img2: "/brand/onboarding/KLR200.png",
    disabled: false,
  },
  {
    kind: "velux_active",
    title: "App Control",
    subtitle: "VELUX Active KIX 300, KIG 300. Not supported in this version yet.",
    img: "/brand/onboarding/velux-active.png",
    img2: null,
    disabled: true,
  },
] as const;

type RemoteKind = (typeof REMOTE_KINDS)[number]["kind"];

/**
 * Looping demonstrations of the three presses that have to be exact: steering
 * the VELUX Touch's remote-type carousel, holding the pin on a settings button for
 * one second, and the two-press key transfer.
 *
 * Null until the files land in public/brand/onboarding/, because a <video>
 * pointing at a missing file renders an empty box, and an empty box is worse
 * than the still it replaced. Fill a path in and that screen switches from
 * still to loop with no other change.
 *
 * These want to be silent looping mp4/webm rather than real .gif files. A few
 * seconds of GIF runs to several megabytes and trips the repo's 1024 KB
 * pre-commit limit; the video renders identically at a fraction of the size.
 */
const LOOPS: Record<"copyMatch" | "copyActive" | "keyTransfer", string | null> = {
  copyMatch: null,
  copyActive: null,
  keyTransfer: null,
};

/** A demonstration loop, falling back to a still while the loop doesn't exist. */
function DemoLoop({
  src,
  fallback,
  alt,
  className,
}: {
  src: string | null;
  fallback: string;
  alt: string;
  className: string;
}) {
  if (src) {
    return (
      <video
        src={src}
        autoPlay
        loop
        muted
        playsInline
        role="img"
        aria-label={alt}
        className={className}
      />
    );
  }
  return <img src={fallback} alt={alt} className={className} />;
}

/** m:ss for the copy-window countdown. */
function formatRemaining(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

interface WindowItem {
  id: string;
  room: string;
  label: string;
  /** Motorised VELUX per the house scan — the pick list shows these first. */
  electric: boolean;
}

const FALLBACK_WINDOWS: WindowItem[] = [
  { id: "w0", room: "Kitchen", label: "Kitchen", electric: true },
  { id: "w1", room: "Living room", label: "Living room", electric: true },
  { id: "w2", room: "Bedroom", label: "Bedroom", electric: true },
  { id: "w3", room: "Bathroom", label: "Bathroom", electric: true },
];

/** The places a discovered actuator can be mapped to, from the house scan.

Every scanned window is derived, and the motorised ones are TAGGED rather than
the manual ones dropped. The pick list shows only the tagged ones, because a
façade window can never be the one that moved and offering it only confuses
(Esben chose from thirteen rows where eleven were impossible). But the full set
stays one tap away behind "I don't see it in this list", because
`window_is_electric` is admin-entered and can be missing: Beta Test House 4 has
two roof windows and none of them marked electric, and a hard filter would
leave its actuators with nowhere to go at all — a dead end, which is worse than
a long list.

Rooms with no window records still get one entry, tagged, so no room is
unmappable. Numbering runs within a room's same-type group, so a lone façade
window beside two roof windows reads "Kitchen · façade window" rather than
"façade window 3", which names nothing. */
function deriveWindows(rooms: Room[]): WindowItem[] {
  const out: WindowItem[] = [];
  rooms.forEach((room, ri) => {
    const roomWindows = room.windows ?? [];
    if (roomWindows.length === 0) {
      out.push({ id: `${ri}`, room: room.name, label: room.name, electric: true });
      return;
    }
    const totals = new Map<string, number>();
    roomWindows.forEach((w) => totals.set(w.window_type, (totals.get(w.window_type) ?? 0) + 1));
    const seen = new Map<string, number>();
    roomWindows.forEach((w, wi) => {
      const typeLabel = w.window_type === "roof" ? "roof window" : "façade window";
      const nth = (seen.get(w.window_type) ?? 0) + 1;
      seen.set(w.window_type, nth);
      const suffix = (totals.get(w.window_type) ?? 1) > 1 ? ` ${nth}` : "";
      out.push({
        id: `${ri}-${wi}`,
        room: room.name,
        label: roomWindows.length > 1 ? `${room.name} · ${typeLabel}${suffix}` : room.name,
        electric: w.window_is_electric === true,
      });
    });
  });
  return out;
}

/**
 * Every stage of this module, in order — the dev screen gallery walks it, and
 * `Stage` is derived from it so the two can never disagree. Add a stage here
 * and it is in the gallery; there is no second list to remember.
 *
 * `copy_expired` and `count_short` are the two recovery screens, listed here
 * rather than hidden inside their neighbours' state so design review can reach
 * them. The rule: a distinct destination reached by a CTA is a stage. The
 * phases inside `identify` are not, because they are one screen cycling through
 * live hardware state within a single visit.
 */
export const STAGES = [
  "intro",
  // The remote-inventory branch (VLX-99): which Touch this install runs
  // through. `remote_check` asks whether a Touch already exists; the own-Touch
  // path continues through the model picker and the coverage question, the
  // kit path through its needs list. See `RemotePath` above for why.
  "remote_check",
  "touch_model",
  "touch_coverage",
  "kit_needs",
  "touch_wake",
  // The wake rescue (VLX-81): a brand-new VELUX Touch whose screen stays
  // blank. A dead-device problem, distinct from the pairing rescue below:
  // its checks are the battery tab and the batteries, not remotes.
  "wake_rescue",
  "copy_start",
  "copy_match",
  "copy_active",
  "copy_expired",
  // The chapter-A pairing rescue (VLX-81): where "the VELUX Touch isn't
  // showing this" leads from the copy screens. A recovery screen in the
  // copy_expired/count_short family, reached from doubt rather than a timer.
  "pairing_rescue",
  "copy_transfer",
  "confirm_count",
  "count_short",
  "klg_intro",
  "klg_setup",
  "klg_check",
  // The KLR 200's five-tap path into sending mode. Only that model visits it;
  // the KLR 300 (kit or owned) reaches sending mode with one held button on
  // klg_transfer itself.
  "klg_send_mode",
  "klg_transfer",
  "discovering",
  "safety",
  "identify",
  "done",
] as const;

export type Stage = (typeof STAGES)[number];

/**
 * The Windows module is a long, multi-step process, so the dots at the top
 * track its three workstreams (not the six global onboarding phases).
 *
 * Each label has to stand alone. It sits on every screen of its part as the
 * only orientation there is, so it cannot lean on the one before it: "Send them
 * to the connector" only resolves if you still remember part one, and the label
 * is what a resident reads precisely because they don't. Name the objects.
 */
const WINDOW_PARTS = [
  "Copy your remotes",
  `Set up the ${PRODUCT_NAMES.windowConnector}`,
  "Find each window",
] as const;

const STAGE_PART: Record<Stage, number> = {
  intro: 0,
  remote_check: 0,
  touch_model: 0,
  touch_coverage: 0,
  kit_needs: 0,
  touch_wake: 0,
  copy_start: 0,
  wake_rescue: 0,
  copy_match: 0,
  copy_active: 0,
  copy_expired: 0,
  pairing_rescue: 0,
  copy_transfer: 0,
  confirm_count: 0,
  count_short: 0,
  klg_intro: 1,
  klg_setup: 1,
  klg_check: 1,
  klg_send_mode: 1,
  klg_transfer: 1,
  discovering: 1,
  safety: 2,
  identify: 2,
  done: 2,
};

/** Dots + caption naming the current workstream, shown under the app bar. */
function WindowsProgress({ stage }: { stage: Stage }) {
  const part = STAGE_PART[stage];
  return (
    <div className="flex flex-col items-center bg-white pb-2">
      <PhaseDots total={WINDOW_PARTS.length} activeIndex={part} />
      <p className="text-muted type-label px-6 text-center">
        {part + 1} · {WINDOW_PARTS[part]}
      </p>
    </div>
  );
}

/**
 * Numbered steps where each carries the pad screen it happens on — the
 * navigate-by-picture rule applied to a menu walk. A step without an image
 * renders as its numbered row alone.
 */
function IllustratedSteps({ steps }: { steps: IllustratedStep[] }) {
  return (
    <div className="flex flex-col gap-5">
      {steps.map((step, i) => (
        <div key={i} className="flex flex-col gap-2.5">
          <div className="flex items-start gap-3">
            <div className="bg-background flex size-6 shrink-0 items-center justify-center rounded-xl">
              <span className="text-primary type-caption">{i + 1}</span>
            </div>
            <p className="text-muted text-[15px] leading-[22px]">{step.text}</p>
          </div>
          {step.img && (
            <div
              className={`flex w-full items-center justify-center rounded-2xl py-3 ${MODULE_HERO}`}
            >
              <img src={step.img} alt="" className="h-[150px] w-auto" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The mirror caption under device-state imagery (VLX-81): the image is a
 * mirror, not a control — Johan pressed a pictured button the moment it
 * appeared. Future tense on purpose. Render it only above a real screen
 * render: over the own-Touch device-photo fallback the claim would be false
 * (a photo of the remote is not what its screen will show).
 */
function DeviceMirrorCaption() {
  return (
    <p className="text-muted type-caption -mt-2 text-center">What your {TOUCH_REMOTE} will show.</p>
  );
}

/** The quiet route into a rescue, identical at every entry point. */
function RescueLink({ onOpen }: { onOpen: () => void }) {
  return <PillButton label={RESCUE_LINK_LABEL} variant="ghost" onClick={onOpen} />;
}

/** Numbered instruction list, matching the pattern in the other compartments. */
function NumberedSteps({ steps }: { steps: string[] }) {
  return (
    <div className="flex flex-col gap-3">
      {steps.map((step, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="bg-background flex size-6 shrink-0 items-center justify-center rounded-xl">
            <span className="text-primary type-caption">{i + 1}</span>
          </div>
          <p className="text-muted text-[15px] leading-[22px]">{step}</p>
        </div>
      ))}
    </div>
  );
}

interface WindowsScreenProps {
  rooms: Room[];
  busy: boolean;
  error: string;
  /** True while the skip is in flight, so its button can't be double-tapped. */
  skipping?: boolean;
  /** Records this compartment as skipped, carrying the error that caused it. */
  onSkip?: (reason: string) => void;
  /** Completes the step; the payload carries the remote-path branch answers. */
  onComplete: (payload?: Record<string, unknown>) => void;
  /**
   * Stage to open on. Production always omits it (each module starts at its
   * own first stage); the dev screen gallery uses it to deep-link a stage.
   */
  initialStage?: Stage;
  /**
   * Actuators to pretend the connector found. Production always omits it and
   * polls Home Assistant for real. The dev gallery passes fixtures because the
   * mapping loop bails straight to `done` when nothing is discovered, which is
   * right in a real house with no windows paired and would otherwise make part
   * three impossible to review without hardware. Mirrors `previewReadings` on
   * the air-sensors screen.
   */
  previewCovers?: WindowCover[];
}

export function WindowsScreen({
  rooms,
  busy,
  error,
  skipping = false,
  onSkip,
  onComplete,
  initialStage,
  previewCovers,
}: WindowsScreenProps) {
  const windows = useMemo(() => {
    const derived = deriveWindows(liveRooms(rooms));
    return derived.length > 0 ? derived : FALLBACK_WINDOWS;
  }, [rooms]);

  const [stage, setStage] = useState<Stage>(initialStage ?? "intro");
  // The remote-inventory branch (VLX-99). Null until `remote_check` is
  // answered; a deep-linked stage (the dev gallery) renders the kit-path
  // variant. The answers travel in the completion payload so support can see
  // which path a household walked instead of guessing.
  const [remotePath, setRemotePath] = useState<RemotePath | null>(null);
  const [pickedKinds, setPickedKinds] = useState<RemoteKind[]>([]);
  const [ownModel, setOwnModel] = useState<OwnTouchModel | null>(null);
  const [ownCoverage, setOwnCoverage] = useState<"all" | "partial" | null>(null);
  // Their Touch is a model the flow can't use (KLR 100). They continue on the
  // kit path — the beta ships a new KLR 300 to every home not known to own a
  // KLR 200/300 — and the flag travels in the payload so support knows an
  // unsupported device is standing in that home.
  const [unsupportedTouch, setUnsupportedTouch] = useState(false);
  // Where the pairing rescue was opened from, so its back button returns the
  // resident to the exact screen they doubted rather than a fixed one. The
  // wake screen has its own rescue stage and doesn't pass through here.
  const [rescueFrom, setRescueFrom] = useState<"copy_start" | "copy_match">("copy_start");
  const openRescue = (from: "copy_start" | "copy_match") => {
    logger.debug("[onboarding] windows: pairing rescue opened from", from);
    setRescueFrom(from);
    setStage("pairing_rescue");
  };

  /**
   * Whether this stage's Touch image is a real screen render, as opposed to
   * the own-Touch device-photo fallback. The mirror caption is only true
   * over the former.
   */
  const touchImageIsRender = (key: TouchImageKey): boolean =>
    !ownTouch || !ownModel || OWN_TOUCH_IMAGES[ownModel][key] != null;
  const ownTouch = remotePath === "own_touch";

  /** The right Touch image for this install: their model's, or the kit's. */
  const touchImage = (key: TouchImageKey): string =>
    ownTouch && ownModel
      ? (OWN_TOUCH_IMAGES[ownModel][key] ?? OWN_TOUCH_PHOTO[ownModel])
      : KIT_TOUCH_IMAGES[key];

  /**
   * The demo loops show the kit's KLR 300; on another model they would
   * demonstrate the wrong device, so those fall back to the still from
   * `touchImage` instead.
   */
  const touchLoop = (src: string | null): string | null =>
    ownTouch && ownModel !== "KLR 300" ? null : src;
  // Part C: window ids already mapped to a room, plus where we are in the
  // per-window rhythm: open one → ask which moved → confirm, user advances.
  const [matched, setMatched] = useState<string[]>([]);
  const [mapPhase, setMapPhase] = useState<
    "opening" | "pick" | "mapped" | "blind" | "nothing_moved"
  >("opening");
  // Which actuator is currently open, and the actuator→window pairs confirmed
  // so far (applied to Home Assistant in one go at the end).
  const [openEntityId, setOpenEntityId] = useState<string | null>(null);
  const [pairs, setPairs] = useState<{ entityId: string; windowId: string }[]>([]);
  // Actuators the resident identified as blinds. The connector lists blinds
  // and windows alike (there is no blind class in it), so without this the
  // loop asked "which window moved" as a blind rolled and had no answer to
  // offer: Beta Test House 5, two blinds, five windows, step skipped. A blind
  // is closed again, set aside, and carried in the step payload; the record
  // has no place for a blind's identity yet, so VELUX connects it later.
  const [blinds, setBlinds] = useState<string[]>([]);
  const [driveError, setDriveError] = useState("");
  // The pick list is filtered to motorised windows; this opens it up again for
  // a house whose scan is missing the flag.
  const [showAllWindows, setShowAllWindows] = useState(false);
  // Names the resident typed on the review screen, keyed by window id, plus
  // which row is being edited and which one is currently being moved to prove
  // it is the right window.
  const [customNames, setCustomNames] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  // The close command a pick fires off, so the next open can wait for it.
  // Only ever one actuator is in flight in this loop, so one slot is enough.
  const closing = useRef<Promise<unknown>>(Promise.resolve());
  // "Open it again" closes and re-opens, so it holds the actuator for a couple
  // of seconds. Picking during that hold has to be blocked, not just ignored.
  const [reopening, setReopening] = useState(false);
  // Which re-open sequence is current. Bumped whenever the screen changes, so
  // a sequence that is mid-gap can tell it has been abandoned.
  const reopenSequence = useRef(0);
  const [applyError, setApplyError] = useState("");
  // Integration reloads already attempted at the discovery stage.
  const [reloadAttempts, setReloadAttempts] = useState(0);
  // When the VELUX Touch's copy window runs out, and a ticking clock to compare it
  // against. The deadline is set by the CTA that confirms the VELUX Touch is open, so
  // the countdown starts on the resident's word rather than on a screen change.
  const [copyDeadline, setCopyDeadline] = useState<number | null>(null);
  const [nowTs, setNowTs] = useState(() => Date.now());

  // Real connector state: polled while checking and during discovery.
  const statusActive = previewCovers == null && (stage === "klg_check" || stage === "discovering");
  const windowsStatus = useWindowsStatus({
    enabled: statusActive,
    refetchInterval: statusActive ? POLL_MS : undefined,
  });
  const rescan = useRescanWindows();
  const drive = useSetWindowPosition();
  const applyMapping = useApplyWindowMapping();

  // Deliberately NOT satisfied by previewCovers: the gallery disables the
  // query, so `klg_check` keeps spinning there instead of advancing off the
  // screen a reviewer just asked to look at.
  const connected = windowsStatus.data?.connected === true;
  const covers = previewCovers ?? windowsStatus.data?.covers ?? [];
  // Actuators still to be identified, in a stable order.
  const unmappedCovers = covers.filter(
    (c) => !pairs.some((p) => p.entityId === c.entity_id) && !blinds.includes(c.entity_id),
  );
  // The loop runs for as many actuators as the connector reports, falling back
  // to the house scan's window count before discovery has answered.
  const totalToMap = covers.length > 0 ? covers.length : windows.length;

  // What the pick list offers. Motorised windows only, plus anything already
  // mapped so a row picked through the escape hatch never disappears from
  // under the resident. A scan with no motorised window at all falls back to
  // the full list rather than showing nothing to pick.
  const electricCount = windows.filter((w) => w.electric).length;
  const pickable =
    showAllWindows || electricCount === 0
      ? windows
      : windows.filter((w) => w.electric || matched.includes(w.id));
  const hiddenCount = windows.length - pickable.length;

  /** A window's name: what the resident typed, or the derived label. */
  const displayName = (w: WindowItem) => customNames[w.id]?.trim() || w.label;

  // House-scan counts shown on the confirm/safety screens. Only motorised
  // products can be learned by the VELUX Touch, so manual windows/blinds don't
  // count. These are always the household's OWN numbers: the design's demo
  // figures stand in only for a household with no rooms at all. (Keying the
  // fallback off "no electric windows" made a scan with a single manual
  // window silently claim 4 windows and 2 blinds.)
  const scanWindows = liveRooms(rooms).flatMap((room) => room.windows ?? []);
  const hasScan = rooms.length > 0;
  const windowCount = hasScan
    ? scanWindows.filter((w) => w.window_is_electric).length
    : FALLBACK_WINDOWS.length;
  const blindCount = hasScan
    ? scanWindows.filter((w) => w.has_blind && w.blind_is_electric).length
    : 2;
  const scanTotal = windowCount + blindCount;

  // A deep-linked copy_active (the dev gallery) has no deadline yet. Give it
  // one so the screen shows a live clock rather than a frozen 0:00.
  useEffect(() => {
    if (stage === "copy_active" && copyDeadline === null) {
      const now = Date.now();
      setNowTs(now);
      setCopyDeadline(now + COPY_WINDOW_MS);
    }
  }, [stage, copyDeadline]);

  // Tick the clock only while it is on screen.
  useEffect(() => {
    if (stage !== "copy_active") {
      return;
    }
    setNowTs(Date.now());
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [stage]);

  const copyRemainingMs =
    copyDeadline === null ? COPY_WINDOW_MS : Math.max(0, copyDeadline - nowTs);

  // Out of time: the VELUX Touch has dropped back to its start screen, so the app's
  // instructions no longer match the device. Say so and walk them back in.
  useEffect(() => {
    if (stage === "copy_active" && copyDeadline !== null && copyRemainingMs === 0) {
      logger.debug("[onboarding] windows: copy window expired");
      setStage("copy_expired");
    }
  }, [stage, copyDeadline, copyRemainingMs]);

  /** Reopening means walking the VELUX Touch's wizard again, from the start. */
  const restartCopy = () => {
    setCopyDeadline(null);
    setStage("copy_start");
  };

  // The connector check waits for Home Assistant to report a loaded KLG 300.
  // A KLR 200 home detours through its five-tap sending-mode walk first; every
  // other Touch reaches sending mode on the transfer screen itself.
  useEffect(() => {
    if (stage === "klg_check" && connected) {
      logger.debug("[onboarding] windows: connector online", windowsStatus.data);
      setStage(ownTouch && ownModel === "KLR 200" ? "klg_send_mode" : "klg_transfer");
    }
  }, [stage, connected, windowsStatus.data, ownTouch, ownModel]);

  // Discovery is done once the connector reports at least one actuator.
  const startDiscovery = () => {
    setReloadAttempts(0);
    setStage("discovering");
  };

  // The key transfer that just happened is what taught the connector about the
  // windows — and the integration enumerates actuators once during setup, with
  // no coordinator — so its entity list is empty by definition when we arrive
  // here. Reload the config entry (no Home Assistant restart needed), retrying
  // a few times in case the connector is still registering them, then leave it
  // to the manual rescan button.
  useEffect(() => {
    if (stage !== "discovering" || covers.length > 0) {
      return;
    }
    if (rescan.isPending || reloadAttempts >= MAX_AUTO_RELOADS) {
      return;
    }
    const timer = setTimeout(
      () => {
        logger.debug("[onboarding] windows: reloading the connector", reloadAttempts + 1);
        setReloadAttempts((n) => n + 1);
        rescan.mutate();
      },
      reloadAttempts === 0 ? 0 : RELOAD_RETRY_MS,
    );
    return () => clearTimeout(timer);
    // `rescan` is a stable mutation object; depending on it would re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, covers.length, rescan.isPending, reloadAttempts]);

  const startMapping = () => {
    setMatched([]);
    setPairs([]);
    // A fresh run forgets the blinds set aside last time too, or the loop
    // would skip them and finish early.
    setBlinds([]);
    setCustomNames({});
    setOpenEntityId(null);
    setDriveError("");
    setMapPhase("opening");
    setStage("identify");
  };

  // Leaving this screen abandons a re-open in flight. The cleanup covers a
  // stage or phase change and unmounting alike, which a disabled back button
  // would not: the sequence has to be able to abandon itself, since by the
  // time it resumes there is no screen left to guard.
  useEffect(
    () => () => {
      reopenSequence.current += 1;
    },
    [stage, mapPhase],
  );

  // Mapping loop: nudge the next actuator open, wait a beat for it to travel,
  // then ask which window moved.
  useEffect(() => {
    if (stage !== "identify" || mapPhase !== "opening") {
      return;
    }
    const next = unmappedCovers[0];
    if (!next) {
      // Nothing left to identify (or discovery found none) — wrap up.
      setStage("done");
      return;
    }
    let cancelled = false;
    setDriveError("");
    // The gallery has no hardware to drive, and a failed command would land the
    // reviewer on the error screen rather than the one they asked for.
    if (previewCovers != null) {
      setOpenEntityId(next.entity_id);
      const timer = setTimeout(() => setMapPhase("pick"), OPEN_SETTLE_MS);
      return () => clearTimeout(timer);
    }
    logger.debug("[onboarding] windows: opening", next.entity_id);
    // Wait for the previous pick's close before opening. "That wasn't it, try
    // again" re-queues the SAME actuator, so without this the open and the
    // still-travelling close are two unordered requests against one motor,
    // and losing that race leaves the window shut under "which one moved?".
    closing.current
      .then(() => drive.mutateAsync({ entityId: next.entity_id, position: NUDGE_POSITION }))
      .then(() => {
        if (cancelled) {
          return;
        }
        setOpenEntityId(next.entity_id);
        setTimeout(() => {
          if (!cancelled) {
            setMapPhase("pick");
          }
        }, OPEN_SETTLE_MS);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        logger.warn("[onboarding] windows: could not open", err);
        setDriveError(getApiErrorMessage(err, "We couldn't reach that window."));
        setMapPhase("nothing_moved");
      });
    return () => {
      cancelled = true;
    };
    // `drive` is a stable mutation object; re-running on it would re-command.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, mapPhase, unmappedCovers[0]?.entity_id]);

  // Picking never auto-opens the next window — the user is standing in a
  // room watching real hardware move, so they set the pace. Each pick lands
  // on a confirmation beat and the next window opens only when they say so.
  const pickWindow = (windowId: string) => {
    if (openEntityId) {
      setPairs((prev) => [...prev, { entityId: openEntityId, windowId }]);
      // Close it again so the house doesn't end up with windows ajar. Not
      // awaited: the resident gets their confirmation beat immediately rather
      // than after a round trip. The promise is kept so the next open can wait
      // for it — see the mapping loop. Catching here also means the stored
      // promise always resolves, so a failed close can never reject a later
      // open that chains onto it.
      closing.current = drive
        .mutateAsync({ entityId: openEntityId, position: 0 })
        .catch((err: unknown) => logger.warn("[onboarding] windows: could not close", err));
    }
    setMatched((prev) => [...prev, windowId]);
    setMapPhase("mapped");
  };

  const loopDone = mappingLoopDone({
    mapped: pairs.length,
    blinds: blinds.length,
    total: totalToMap,
    remaining: unmappedCovers.length,
  });

  const openNextWindow = () => {
    setOpenEntityId(null);
    if (loopDone) {
      setStage("done");
    } else {
      setMapPhase("opening");
    }
  };

  /** The product that moved was a blind. Close it, set it aside, move on. */
  const setAsideBlind = () => {
    if (openEntityId) {
      const entityId = openEntityId;
      setBlinds((prev) => (prev.includes(entityId) ? prev : [...prev, entityId]));
      closing.current = drive
        .mutateAsync({ entityId, position: 0 })
        .catch((err: unknown) => logger.warn("[onboarding] windows: could not close blind", err));
    }
    setMapPhase("blind");
  };

  /** Move the window that is currently open, again, without changing the turn.
   *
   * Someone who looked away, or who has two identical roof windows in one
   * room, needs a second showing more than they need to guess. */
  const reopenCurrent = async () => {
    if (!openEntityId || reopening || previewCovers != null) {
      return;
    }
    const sequence = reopenSequence.current;
    setDriveError("");
    setReopening(true);
    try {
      await drive.mutateAsync({ entityId: openEntityId, position: 0 });
      await new Promise((resolve) => setTimeout(resolve, REOPEN_GAP_MS));
      // Back, or the step ending, lands in the gap while the window is shut.
      // Opening it now would move a window behind a resident who has already
      // left the screen, and nothing would ever close it again.
      if (reopenSequence.current !== sequence) {
        return;
      }
      await drive.mutateAsync({ entityId: openEntityId, position: NUDGE_POSITION });
    } catch (err: unknown) {
      logger.warn("[onboarding] windows: could not re-open", err);
      setDriveError(getApiErrorMessage(err, "We couldn't reach that window."));
    } finally {
      setReopening(false);
    }
  };

  /** Undo one mapping and put its actuator back in the queue.
   *
   * The actuator returns to `unmappedCovers` in its original order, so the
   * loop offers it again rather than skipping it. Called from the confirmation
   * beat ("that wasn't it") and from the review list. */
  const remapWindow = (windowId: string) => {
    setPairs((prev) => prev.filter((p) => p.windowId !== windowId));
    setMatched((prev) => prev.filter((id) => id !== windowId));
    setCustomNames((prev) => {
      const { [windowId]: _dropped, ...rest } = prev;
      return rest;
    });
    setEditingId(null);
    setOpenEntityId(null);
    setDriveError("");
    setMapPhase("opening");
    setStage("identify");
  };

  /** Open a window that is already mapped, then close it again.
   *
   * The green check is the same on every row, so this is the only way to
   * confirm a pick was right — and the only way to name two identical windows
   * in one room with any confidence. */
  const verifyWindow = async (windowId: string) => {
    const entityId = pairs.find((p) => p.windowId === windowId)?.entityId;
    if (!entityId || previewCovers != null) {
      return;
    }
    setDriveError("");
    setVerifyingId(windowId);
    try {
      await drive.mutateAsync({ entityId, position: NUDGE_POSITION });
      await new Promise((resolve) => setTimeout(resolve, VERIFY_HOLD_MS));
      await drive.mutateAsync({ entityId, position: 0 });
    } catch (err: unknown) {
      logger.warn("[onboarding] windows: could not verify", err);
      setDriveError(getApiErrorMessage(err, "We couldn't reach that window."));
    } finally {
      setVerifyingId(null);
    }
  };

  /** The branch answers, persisted with the step so support can see the path. */
  const completionPayload = {
    remote_kinds: pickedKinds,
    remote_path: remotePath ?? "kit_touch",
    own_touch_model: ownModel,
    own_touch_coverage: ownCoverage,
    own_touch_unsupported: unsupportedTouch,
    // Blinds the resident set aside during the mapping, by actuator, so the
    // identities survive for whoever connects them later.
    blinds_set_aside: blinds,
  };

  /** Push the actuator→room mapping to HA (renames + areas) and finish. */
  const finish = async () => {
    setApplyError("");
    const mappings = pairs
      .map(({ entityId, windowId }) => ({
        entity_id: entityId,
        room_name: windows.find((w) => w.id === windowId)?.room ?? "",
        // Only a name the resident actually typed travels. Sending the derived
        // label instead would slugify "Kitchen · roof window 1" into an entity
        // id, so leaving it out keeps the default naming exactly as it was.
        label: customNames[windowId]?.trim() || null,
      }))
      .filter((m) => m.room_name);
    if (mappings.length === 0) {
      onComplete(completionPayload);
      return;
    }
    try {
      const result = await applyMapping.mutateAsync(mappings);
      logger.debug("[onboarding] windows mapping applied", result);
      onComplete(completionPayload);
    } catch (err) {
      setApplyError(getApiErrorMessage(err, "Could not save the mapping to SmartClimate."));
    }
  };

  // ── 1 · intro — why the compartment. The needs list moved to `kit_needs`:
  //        what a household needs depends on the remote_check answer, and a
  //        list shown before the question would promise a kit Touch to homes
  //        that route through their own. ─────────────────────────────────────
  if (stage === "intro") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="windows" stage={stage} title={PRODUCT_NAMES.windows} />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className={`h-[173px] w-full shrink-0 overflow-hidden rounded-2xl ${MODULE_HERO}`}>
            <img
              src="/brand/onboarding/windows-intro-photo.jpg"
              alt="Someone reading under a VELUX roof window"
              className="size-full object-cover object-[center_60%]"
            />
          </div>
          <h1 className="text-primary type-title">Let&apos;s connect your VELUX windows</h1>
          <p className="text-muted text-[17px] leading-6">
            <span className="font-bold">Why this part?</span> Right now only your VELUX remotes can
            move your windows. Once SmartClimate can reach them, it opens a window when a room needs
            fresh air and closes it again when the air is fresh.
          </p>
          <p className="text-primary text-[17px] leading-6">
            Close all your VELUX windows before you start.
          </p>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="Continue" onClick={() => setStage("remote_check")} />
        </div>
      </div>
    );
  }

  // ── 1b · which remotes exist in this home (VLX-99) ───────────────────────
  //
  // Asked as behaviour ("how do you open your windows today"), with pictures,
  // because a resident recognises the remote on their wall and does not know
  // its model name. Multi-select: homes own mixes, and which path wins is our
  // rule to apply (a Touch outranks wall switches), not theirs to work out.
  // VELUX Active stays visible but disabled — an Active household that
  // couldn't find itself in the list would guess into a path that dead-ends,
  // and a visible "not yet" sets the expectation instead.
  if (stage === "remote_check") {
    const toggleKind = (kind: RemoteKind) => {
      setPickedKinds((prev) =>
        prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind],
      );
    };
    const continueFromKinds = () => {
      // The VELUX Touch decides the route: keys can never leave one (VLX-72), so a
      // home that has one must run the install through it, wall switches or
      // not. Wall switches alone take the kit's new Touch.
      const path: RemotePath = pickedKinds.includes("velux_touch") ? "own_touch" : "kit_touch";
      logger.debug("[onboarding] windows: remote kinds", pickedKinds, path);
      setRemotePath(path);
      // Every re-answer starts the branch clean: without this, someone who
      // walked into the own-Touch path, backed out and re-answered would
      // complete the step with a stale model/coverage stapled to the kit
      // path, and support would read a contradiction.
      setOwnModel(null);
      setOwnCoverage(null);
      setUnsupportedTouch(false);
      setStage(path === "own_touch" ? "touch_model" : "kit_needs");
    };
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("intro")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">
              How do you open your VELUX windows today?
            </h1>
            <p className="text-muted text-[17px] leading-6">
              Select everything you use. The next steps depend on it.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {REMOTE_KINDS.map(({ kind, title, subtitle, img, img2, disabled }) => {
              const picked = pickedKinds.includes(kind);
              if (disabled) {
                // Disabled, not hidden. aria-disabled instead of the disabled
                // attribute so screen readers still announce the option, with
                // the subtitle explaining the state.
                //
                // An Active-only household would have nothing to select here,
                // but that home does not exist: every VELUX install ships with
                // physical remotes (wall switches or a Touch), Active or not,
                // so at least one selectable card is always true. Deliberate,
                // per Francis (28 Aug 2026) — do not add an escape hatch for
                // a household that cannot occur.
                return (
                  <div
                    key={kind}
                    aria-disabled="true"
                    className="border-border flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left opacity-50"
                  >
                    <span className="flex shrink-0 items-center gap-1">
                      <img src={img} alt="" className="h-14 w-14 object-contain" />
                      {img2 && <img src={img2} alt="" className="h-14 w-14 object-contain" />}
                    </span>
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-primary text-[17px] leading-6">{title}</span>
                      <span className="text-muted type-body-sm">{subtitle}</span>
                    </span>
                  </div>
                );
              }
              return (
                <button
                  key={kind}
                  type="button"
                  aria-pressed={picked}
                  onClick={() => toggleKind(kind)}
                  className={cn(
                    "flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left transition-colors",
                    picked ? "border-accent" : "border-border hover:border-accent",
                  )}
                >
                  <span className="flex shrink-0 items-center gap-1">
                    <img src={img} alt="" className="h-14 w-14 object-contain" />
                    {img2 && <img src={img2} alt="" className="h-14 w-14 object-contain" />}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-primary text-[17px] leading-6">{title}</span>
                    <span className="text-muted type-body-sm">{subtitle}</span>
                  </span>
                  {/* The slot is reserved either way so a card doesn't shift
                      when picked; only the check's visibility changes. */}
                  <span
                    className={cn(
                      "flex size-6 shrink-0 items-center justify-center rounded-full",
                      picked ? "bg-accent" : "border-border border",
                    )}
                  >
                    {picked && <Check className="size-4 text-white" strokeWidth={3} />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton
            label="Continue"
            disabled={pickedKinds.length === 0}
            onClick={continueFromKinds}
          />
        </div>
      </div>
    );
  }

  // ── 1c · which model their VELUX Touch is — the models pair differently ────────
  if (stage === "touch_model") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("remote_check")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Which {TOUCH_REMOTE} do you have?</h1>
            {/* The picture is the answer; the model plate is the fallback for
                anyone whose remote looks like none of them. */}
            <p className="text-muted text-[17px] leading-6">
              Not sure? The model name is printed on the back of the remote.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {OWN_TOUCH_MODELS.map(({ model, img, caption, disabled }) =>
              disabled ? (
                // Disabled, not hidden, same as the Active card on
                // remote_check: a KLR 100 owner has to see their model named
                // and ruled out, or they'd pick the nearest lookalike and
                // strand on instructions for a device they don't have.
                <div
                  key={model}
                  aria-disabled="true"
                  className="border-border flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left opacity-50"
                >
                  {img && <img src={img} alt="" className="h-14 w-14 shrink-0 object-contain" />}
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-primary text-[17px] leading-6">{model}</span>
                    <span className="text-muted type-body-sm">{caption}</span>
                  </span>
                </div>
              ) : (
                <button
                  key={model}
                  type="button"
                  onClick={() => {
                    logger.debug("[onboarding] windows: own touch model", model);
                    setOwnModel(model);
                    setStage("touch_coverage");
                  }}
                  className="border-border hover:border-accent flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left transition-colors"
                >
                  {img && <img src={img} alt="" className="h-14 w-14 shrink-0 object-contain" />}
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-primary text-[17px] leading-6">{model}</span>
                    <span className="text-muted type-body-sm">{caption}</span>
                  </span>
                </button>
              ),
            )}
          </div>
        </div>
        {/* The exit for a KLR 100 (or unrecognised) Touch, so an unsupported
            model is a route and not a dead end: those homes continue on the
            kit path with the new KLR 300 the beta ships them, copying their
            plain remotes onto it. ASSUMPTION for Simon to confirm: KLI keys
            bound to a KLR 100 can still be copied to a new Touch — VLX-72's
            key-exclusivity was observed between Touches. */}
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton
            label="My VELUX Touch isn't supported"
            variant="ghost"
            onClick={() => {
              logger.debug("[onboarding] windows: own touch unsupported, rerouting to kit path");
              setUnsupportedTouch(true);
              setRemotePath("kit_touch");
              setOwnModel(null);
              setOwnCoverage(null);
              setStage("kit_needs");
            }}
          />
        </div>
      </div>
    );
  }

  // ── 1d · can their VELUX Touch already move everything? ────────────────────────
  //
  // Answered by doing, not remembering: "try each window now" turns a memory
  // question into a test the resident runs on the spot, and catches the
  // partial home before it strands at the connector step with missing keys —
  // the cheapest point to catch it, same principle as the air sensors' wave
  // test.
  if (stage === "touch_coverage") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("touch_model")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div
            className={`flex h-[220px] w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl ${MODULE_HERO}`}
          >
            <img
              src={touchImage("home")}
              alt={`Your ${TOUCH_REMOTE}`}
              className="h-[185px] w-auto"
            />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">
              Can your {TOUCH_REMOTE} move every window?
            </h1>
            <p className="text-muted text-[17px] leading-6">
              Pick it up and try each VELUX window and blind now. Every one it can move is already
              set up right.
            </p>
            <p className="text-muted text-[17px] leading-6">
              If some don&apos;t respond, we&apos;ll copy those onto your {TOUCH_REMOTE} first.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
          <PillButton
            label="It moves all of them"
            onClick={() => {
              logger.debug("[onboarding] windows: own touch coverage", "all");
              setOwnCoverage("all");
              setStage("klg_intro");
            }}
          />
          <PillButton
            label="Some don't respond"
            variant="ghost"
            onClick={() => {
              logger.debug("[onboarding] windows: own touch coverage", "partial");
              setOwnCoverage("partial");
              setStage("copy_start");
            }}
          />
        </div>
      </div>
    );
  }

  // ── 1e · what the kit path needs — the old intro list, after the branch ──
  if (stage === "kit_needs") {
    const needs = [
      {
        title: "Your VELUX remotes",
        caption: "Every remote you use for VELUX windows and blinds.",
        img: "/brand/onboarding/windows-remote.png",
      },
      {
        title: `${TOUCH_REMOTE} remote`,
        caption: "In your kit.",
        img: "/brand/onboarding/windows-touch-home.png",
      },
      {
        title: "The pin",
        caption: "In your kit.",
        img: "/brand/onboarding/windows-pin.png",
      },
    ];
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("remote_check")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          {/* The heading confirms the branch answer rather than labelling the
              list (the list draws its own "what you'll need" eyebrow): the
              resident just said they have no Touch, and this is the screen
              that tells them what that means. */}
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">
              You&apos;ll use the kit&apos;s {TOUCH_REMOTE}
            </h1>
            <p className="text-muted text-[17px] leading-6">
              Your remotes hand their window keys to the new {TOUCH_REMOTE}, and it carries them to
              SmartClimate.
            </p>
          </div>
          <MaterialsNeeded items={needs} />
          <p className="text-primary text-[17px] leading-6">
            Make sure you have everything ready before we start.
          </p>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="I have everything" onClick={() => setStage("touch_wake")} />
        </div>
      </div>
    );
  }

  // ── 2 · wake the VELUX Touch (battery tab → region). Kit path only: a household's
  //        own Touch is already awake on their wall. ──────────────────────────
  if (stage === "touch_wake") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("kit_needs")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div
            className={`flex h-[242px] w-full shrink-0 items-center justify-center gap-2 overflow-hidden rounded-2xl px-3 ${MODULE_HERO}`}
          >
            <img
              src="/brand/onboarding/windows-remote-tab.png"
              alt="Pull the battery tab on the back of the VELUX Touch"
              className="h-[165px] w-auto -translate-y-6"
            />
            <img
              src="/brand/onboarding/windows-touch-map.png"
              alt="The VELUX Touch showing the world map"
              className="h-[140px] w-auto translate-y-6"
            />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Wake up your {TOUCH_REMOTE}</h1>
            <p className="text-muted text-[17px] leading-6">
              Take the {TOUCH_REMOTE} from the kit and pull the battery tab on the back to connect
              the batteries.
            </p>
            <p className="text-muted text-[17px] leading-6">
              A world map appears on its screen. Pick <span className="text-primary">Europe</span>{" "}
              with the arrows and confirm with the checkmark.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
          <PillButton label="The VELUX Touch is awake" onClick={() => setStage("copy_start")} />
          <RescueLink onOpen={() => setStage("wake_rescue")} />
        </div>
      </div>
    );
  }

  // ── 3 · the VELUX Touch offers to copy — one session covers every remote ───────
  //
  // Two variants of one screen. The kit path describes the new KLR 300's
  // first-run wizard (it offers to copy right after waking). A household's own
  // Touch is long past its first run, so that path reads from the per-model
  // entry steps instead — see OWN_TOUCH_COPY_ENTRY, pending Nico's document.
  if (stage === "copy_start") {
    const ownEntrySteps = ownTouch
      ? ((ownModel && OWN_TOUCH_COPY_ENTRY[ownModel]) ?? OWN_TOUCH_COPY_FALLBACK)
      : null;
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage(ownTouch ? "touch_coverage" : "touch_wake")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div
            className={`flex h-[242px] w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl ${MODULE_HERO}`}
          >
            <img
              src={touchImage("copy")}
              alt="The VELUX Touch offering to copy from another remote"
              className="h-[205px] w-auto"
            />
          </div>
          {touchImageIsRender("copy") && <DeviceMirrorCaption />}
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">
              {ownTouch
                ? `Copy the missing remotes into your ${TOUCH_REMOTE}`
                : "Copy your remotes into the VELUX Touch"}
            </h1>
            <p className="text-muted text-[17px] leading-6">
              {ownTouch
                ? "Your VELUX Touch will copy the signal from the remotes it doesn't answer to yet."
                : "The VELUX Touch will copy the signal from the remotes you already own."}
            </p>
            <p className="text-muted text-[17px] leading-6">
              You do one round for each type of remote. Four remotes of the same type is one round.
            </p>
            {/* Normalise the retry before anything can fail (VLX-81): a first
                try that silently does nothing sent Johan into a guessing
                spiral. Said here, silence later reads as "try again", not
                "broken". */}
            <p className="text-muted text-[17px] leading-6">
              Copying often takes more than one try. That&apos;s normal.
            </p>
            {ownEntrySteps ? (
              <IllustratedSteps steps={ownEntrySteps} />
            ) : (
              <p className="text-muted text-[17px] leading-6">Tap the forward arrow.</p>
            )}
            <p className="text-primary text-[17px] leading-6">
              {ownTouch
                ? "Fetch every remote your VELUX Touch doesn't answer to. Blind remotes too."
                : "Fetch every VELUX remote before the next step. Blind remotes too."}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
          <PillButton
            label={ownEntrySteps ? "The VELUX Touch offers to copy" : "I've tapped the arrow"}
            onClick={() => setStage("copy_match")}
          />
          <RescueLink onOpen={() => openRescue("copy_start")} />
        </div>
      </div>
    );
  }

  // ── 4 · steer the carousel to the remote in their hand, then confirm ─────
  if (stage === "copy_match") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("copy_start")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div
            className={`flex h-[242px] w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl ${MODULE_HERO}`}
          >
            <DemoLoop
              src={touchLoop(LOOPS.copyMatch)}
              fallback={touchImage("select")}
              alt="Stepping through the remote types on the VELUX Touch with the arrows, then confirming with the checkmark"
              className="h-[205px] w-auto"
            />
          </div>
          {touchImageIsRender("select") && <DeviceMirrorCaption />}
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Find your remote on the VELUX Touch</h1>
            <p className="text-muted text-[17px] leading-6">
              The VELUX Touch shows one type of remote at a time. Use the back and forward arrows
              until the picture matches the remote in your hand.
            </p>
            <p className="text-muted text-[17px] leading-6">
              When it matches, tap the checkmark. That opens the VELUX Touch for ten minutes, and
              you press your remotes during that time.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
          <PillButton
            label="The VELUX Touch is open"
            onClick={() => {
              // Both from one reading of the clock. Taking `now` here and
              // leaving `nowTs` at its mount value paints one frame of
              // "13:00 left" for a resident who spent three minutes getting
              // here, on the one screen whose whole job is being trusted
              // about time.
              const now = Date.now();
              setNowTs(now);
              setCopyDeadline(now + COPY_WINDOW_MS);
              setStage("copy_active");
            }}
          />
          <RescueLink onOpen={() => openRescue("copy_match")} />
        </div>
      </div>
    );
  }

  // ── 5 · the copy window — the one thing only the app can track ───────────
  if (stage === "copy_active") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="windows" stage={stage} title={PRODUCT_NAMES.windows} />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className="flex flex-col items-center gap-1">
            <p className="text-primary type-title tabular-nums">
              {formatRemaining(copyRemainingMs)} left
            </p>
            <p className="text-muted type-body-sm text-center">
              If time runs out, no harm done. We&apos;ll reopen it.
            </p>
          </div>
          <div
            className={`flex h-[190px] w-full shrink-0 items-center justify-center gap-1 overflow-hidden rounded-2xl px-3 ${MODULE_HERO}`}
          >
            <DemoLoop
              src={LOOPS.copyActive}
              fallback="/brand/onboarding/windows-remote-settings.png"
              alt="Holding the pin on the settings button of a VELUX remote for one second"
              className="h-[165px] w-auto"
            />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Press each remote with the pin</h1>
            {/* Locate before instruct: a beta user (Michael) never noticed the
                pin was in the kit, so the step that first needs it says where
                it lives rather than assuming the kit overview stuck. */}
            <p className="text-muted text-[17px] leading-6">
              You&apos;ll find the pin in compartment 3. Hold it on the settings button for one
              second. One second is enough, do not hold it longer.
            </p>
            <p className="text-muted text-[17px] leading-6">
              Do every remote of this type, then come back here.
            </p>
          </div>
          <HelpHint
            collapsible={false}
            question="Can't find the settings button?"
            answer="Look on the side or the back of the remote, behind a small hole."
          />
        </div>
        {/* Sticky, because the second button is the only route onward for a
            household with more than one type of remote, and unstuck it sits
            49px below the fold on a 812px screen. */}
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-7">
          <PillButton label="That's all my remotes" onClick={() => setStage("copy_transfer")} />
          {/* The VELUX Touch copies one type of remote per round, so a household with
              a wall dial and a handheld comes back here and picks the second
              type. `copy_match` sets a fresh deadline on the way out, which is
              right: tapping the checkmark again opens the VELUX Touch for another ten
              minutes. */}
          <PillButton
            label="I have another type of remote"
            variant="ghost"
            onClick={() => setStage("copy_match")}
          />
        </div>
      </div>
    );
  }

  // ── 5b · the ten minutes ran out — walk them back in, don't just say so ──
  if (stage === "copy_expired") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="windows" stage={stage} title={PRODUCT_NAMES.windows} />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-6 px-7 pt-6">
          <div className="flex items-center justify-center pt-2">
            <div className="bg-surface-subtle flex size-[140px] items-center justify-center rounded-full">
              <TriangleAlert className="text-accent size-12" strokeWidth={2} />
            </div>
          </div>
          <div className="flex flex-col gap-2 text-center">
            <h1 className="text-primary type-headline">The VELUX Touch has closed</h1>
            <p className="text-muted text-[17px] leading-6">
              It stays open for ten minutes at a time, so it closes itself when the time is up. No
              harm done, and you carry on from where you stopped.
            </p>
            <p className="text-muted text-[17px] leading-6">
              We&apos;ll take you back through opening it.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
          <PillButton label="Open the VELUX Touch again" onClick={restartCopy} />
          <StuckHelp
            stepKey="windows"
            stage="copy_expired"
            seed={`The ${TOUCH_REMOTE} closed before I finished copying my remotes. Can you help?`}
            label="Get help"
          />
        </div>
      </div>
    );
  }

  // ── 2b · the wake rescue — a blank brand-new VELUX Touch (VLX-81) ────────
  //
  // A dead device, not a mispairing: the checks are the tab and the
  // batteries, and remotes are never mentioned. Its own stage rather than a
  // mode of the pairing rescue, so the design gallery can reach it and its
  // return target is unambiguous.
  if (stage === "wake_rescue") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("touch_wake")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">When the {TOUCH_REMOTE} stays blank</h1>
            <p className="text-muted text-[17px] leading-6">
              A brand-new {TOUCH_REMOTE} can need a nudge to wake. Work through these in order.
            </p>
          </div>
          <NumberedSteps steps={WAKE_RESCUE_STEPS} />
        </div>
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-7">
          <PillButton label="The screen is on" onClick={() => setStage("touch_wake")} />
          <StuckHelp
            stepKey="windows"
            stage="wake_rescue"
            // No claim that the checks were done: a resident may ask before
            // working the list, and a seed that says batteries were swapped
            // would steer the assistant past the most likely fixes.
            seed={`My ${TOUCH_REMOTE}'s screen stays blank after pulling the battery tab.`}
            label="Checked everything? Ask me"
          />
        </div>
      </div>
    );
  }

  // ── 5c · the pairing rescue — a route, not a paragraph (VLX-81) ──────────
  //
  // Johan's biggest time sink: a silent failure sent him guessing (distance?
  // batteries? wait or retry?) with nothing to check against. This screen
  // makes the decisions for him, in evidence order, and opens by normalising
  // the retry, because the resident arriving here believes something is
  // broken and usually nothing is.
  if (stage === "pairing_rescue") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage(rescueFrom)}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">
              When the {TOUCH_REMOTE} doesn&apos;t respond
            </h1>
            <p className="text-muted text-[17px] leading-6">
              Pairing often takes more than one try. That&apos;s normal, not a fault. Work through
              these in order.
            </p>
          </div>
          <NumberedSteps steps={PAIRING_RESCUE_STEPS} />
        </div>
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-7">
          <PillButton label="Start the copy again" onClick={restartCopy} />
          <StuckHelp
            stepKey="windows"
            stage="pairing_rescue"
            seed={`My ${TOUCH_REMOTE} isn't showing what the app describes while I copy my remotes.`}
            label="Checked everything? Ask me"
          />
        </div>
      </div>
    );
  }

  // ── 6 · finalize on the VELUX Touch — keys transfer (up to 5 min) ──────────────
  if (stage === "copy_transfer") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("copy_active")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div
            className={`flex h-[242px] w-full shrink-0 items-center justify-center gap-4 overflow-hidden rounded-2xl px-4 ${MODULE_HERO}`}
          >
            <img
              src={touchImage("check")}
              alt="The VELUX Touch checkmark screen"
              className="h-[140px] w-auto -translate-y-5"
            />
            <img
              src={touchImage("keys")}
              alt="The VELUX Touch transferring keys"
              className="h-[140px] w-auto translate-y-5"
            />
          </div>
          {touchImageIsRender("check") && touchImageIsRender("keys") && <DeviceMirrorCaption />}
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Let the VELUX Touch collect the keys</h1>
            <p className="text-muted text-[17px] leading-6">
              Tap the checkmark on the VELUX Touch. It now gathers the keys from every remote you
              pressed.
            </p>
            <p className="text-muted text-[17px] leading-6">
              This takes up to five minutes. Leave it to work.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
          <PillButton
            label="The VELUX Touch is finished"
            onClick={() => setStage("confirm_count")}
          />
          {/* The one screen in this module that promises a multi-minute wait
              with no channel out: unlike the connector check, the VELUX Touch's own
              progress is invisible to the app, so there is no poll to watch
              and nothing but this timer to notice the resident has been
              sitting here a while. */}
          <StuckHelp
            stepKey="windows"
            stage={stage}
            seed={`I tapped the checkmark to let the ${TOUCH_REMOTE} collect the keys, and it has been a while. Is that normal?`}
            label="Taking a while? Ask me"
            afterMs={STUCK_HELP_MS}
          />
        </div>
      </div>
    );
  }

  // ── 7 · state the number we expect, and ask for a yes or no ──────────────
  if (stage === "confirm_count") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("copy_transfer")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div
            className={`flex h-[220px] w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl ${MODULE_HERO}`}
          >
            <img
              src={touchImage("count")}
              alt="The VELUX Touch showing how many windows and blinds it paired"
              className="h-[185px] w-auto"
            />
          </div>
          {touchImageIsRender("count") && <DeviceMirrorCaption />}
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Check the number on the VELUX Touch</h1>
            <p className="text-muted text-[17px] leading-6">
              {scanTotal > 0
                ? `Your house scan lists ${describeScan(windowCount, blindCount)}. The VELUX Touch should show the same.`
                : "Your house scan doesn't list any motorised VELUX products, so check the number against what you actually have."}
            </p>
          </div>
          {/* These two rows are a transcription of the VELUX Touch's own screen, which
              prints "+4" beside a window glyph and "+2" beside a blind glyph.
              So they keep the digit and the plus, against the spell-out rule:
              the resident is comparing the phone to the device, and a word they
              have to convert is one more step in the comparison. The sentence
              above stays spelled, because that is prose about their house scan
              and nothing displays it. */}
          {scanTotal > 0 && (
            <div className="flex flex-col gap-4">
              {windowCount > 0 && (
                <div className="flex items-center gap-5">
                  <img
                    src="/brand/onboarding/windows-icon-window.png"
                    alt=""
                    className="h-11 w-12 rounded object-cover"
                  />
                  <p className="text-muted flex-1 text-[17px] leading-6">
                    <span className="text-primary">+{windowCount}</span> VELUX{" "}
                    {windowCount === 1 ? "window" : "windows"}
                  </p>
                </div>
              )}
              {blindCount > 0 && (
                <div className="flex items-center gap-5">
                  <img
                    src="/brand/onboarding/windows-icon-house.png"
                    alt=""
                    className="h-11 w-12 rounded object-cover"
                  />
                  <p className="text-muted flex-1 text-[17px] leading-6">
                    <span className="text-primary">+{blindCount}</span> VELUX{" "}
                    {blindCount === 1 ? "blind" : "blinds"}
                  </p>
                </div>
              )}
            </div>
          )}
          <p className="text-muted text-[17px] leading-6">
            If it matches, tap the checkmark on the VELUX Touch. Then the home button, then End
            Setup.
          </p>
        </div>
        <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
          <PillButton label="The numbers match" onClick={() => setStage("klg_intro")} />
          <PillButton
            label="It's showing fewer"
            variant="ghost"
            onClick={() => setStage("count_short")}
          />
        </div>
      </div>
    );
  }

  // ── 7b · a remote didn't copy — a route, not a paragraph to unfold ───────
  if (stage === "count_short") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("confirm_count")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div
            className={`flex h-[190px] w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl ${MODULE_HERO}`}
          >
            <img
              src="/brand/onboarding/windows-remote-settings.png"
              alt="Where to find the settings button on a VELUX remote"
              className="h-[165px] w-auto"
            />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">One of the remotes didn&apos;t copy</h1>
            <p className="text-muted text-[17px] leading-6">
              No need to start over. On the VELUX Touch, tap the checkmark, then the back arrow, and
              pick the remote that&apos;s missing.
            </p>
            <p className="text-muted text-[17px] leading-6">
              Press its settings button with the pin for one second. Then look at the number again.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
          <PillButton label="The numbers match now" onClick={() => setStage("klg_intro")} />
          {/* The chat leads from here: it reads the record back (windows per
              room, blinds) and asks whether it is right, then what controls
              each window. The resident only confirms or corrects. */}
          <StuckHelp
            stepKey="windows"
            stage="count_short"
            seed={`The ${TOUCH_REMOTE} is showing fewer windows than my house scan and redoing the remote didn't fix it.`}
            carryOn={`Tap "The numbers match now" and carry on with the windows the ${TOUCH_REMOTE} shows.`}
            label="Still wrong. Get help"
          />
        </div>
      </div>
    );
  }

  // ── 8 · why the connector + what part two needs ──────────────────────────
  if (stage === "klg_intro") {
    // The own-all path arrives here straight from the coverage question, so
    // its Touch caption and back target differ; the pin caption stops saying
    // "the same one" when no earlier screen introduced a pin.
    const skippedCopy = ownTouch && ownCoverage === "all";
    const needs = [
      {
        title: CONNECTOR,
        caption: "With its ethernet cable and power cable.",
        img: "/brand/onboarding/windows-connector.png",
      },
      {
        title: TOUCH_REMOTE,
        caption: ownTouch
          ? "Your own, the one your windows answer to."
          : "The one you just set up.",
        img: touchImage("home"),
      },
      {
        title: "The pin",
        caption: skippedCopy ? "In your kit." : "The same one.",
        img: "/brand/onboarding/windows-pin.png",
      },
    ];
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage(skippedCopy ? "touch_coverage" : "confirm_count")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className={`h-[173px] w-full shrink-0 overflow-hidden rounded-2xl ${MODULE_HERO}`}>
            <img
              src="/brand/onboarding/windows-intro-photo.jpg"
              alt="Someone reading under a VELUX roof window"
              className="size-full object-cover object-[center_60%]"
            />
          </div>
          <h1 className="text-primary type-headline">Connect your windows to SmartClimate</h1>
          <p className="text-muted text-[17px] leading-6">
            <span className="font-bold">Why this part?</span> We&apos;re going to set up the{" "}
            {CONNECTOR} now. It sits next to your router, and it&apos;s what lets SmartClimate open
            and close your windows based on the air in each room.
          </p>
          <MaterialsNeeded items={needs} />
          {/* Some kits ship without a new Touch once the pre-ship inventory
              knows the home owns one; phrased conditionally so it stays true
              either way. No return logistics in the beta (Francis, 26 Aug). */}
          {ownTouch && (
            <p className="text-muted text-[17px] leading-6">
              If your kit includes a new {TOUCH_REMOTE}, it stays in its compartment. Keep it as a
              spare.
            </p>
          )}
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="I have everything" onClick={() => setStage("klg_setup")} />
        </div>
      </div>
    );
  }

  // ── 9 · cable up the connector ───────────────────────────────────────────
  if (stage === "klg_setup") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("klg_intro")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div
            className={`flex h-[242px] w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl ${MODULE_HERO}`}
          >
            <img
              src="/brand/onboarding/windows-connector.png"
              alt={`The ${CONNECTOR}`}
              className="h-[190px] w-auto drop-shadow-md"
            />
          </div>
          <h1 className="text-primary type-headline">Set up the {CONNECTOR}</h1>
          <NumberedSteps
            steps={[
              `Plug the white ethernet cable into the connector, and the other end into a free port on your router or the network switch.`,
              `Plug the connector into your power strip.`,
              // White, not green. Observed on a real KLG 300 in the field. The
              // copy claimed green from the start and nobody had watched one
              // boot. A wrong colour is worse here than no colour: someone
              // waiting for green watches a light that never turns green and
              // concludes the connector is broken.
              "Wait for the power light to turn solid white.",
            ]}
          />
        </div>
        <div className="px-6 pt-4 pb-7">
          <PillButton label="The light is white" onClick={() => setStage("klg_check")} />
        </div>
      </div>
    );
  }

  // ── 10 · reach the connector (real check against Home Assistant) ─────────
  if (stage === "klg_check") {
    const waitingDetail = windowsStatus.error
      ? getApiErrorMessage(windowsStatus.error, "We can't reach the connector yet.")
      : formatStatusDetail(windowsStatus.data?.detail);
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("klg_setup")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-6 px-7 pt-6">
          <div className="flex items-center justify-center pt-2">
            <div className="bg-sky-subtle flex size-[140px] items-center justify-center rounded-full">
              <Loader2 className="text-sky size-12 animate-spin" strokeWidth={2.5} />
            </div>
          </div>
          <div className="flex flex-col gap-2 text-center">
            <h1 className="text-primary type-headline">Checking the {CONNECTOR}</h1>
            <p className="text-muted text-[15px] leading-[22px]">
              {waitingDetail ?? `We're making sure the ${PRODUCT_NAMES.hub} can see it.`}
            </p>
          </div>
          <StuckHelp
            stepKey="windows"
            stage="klg_check"
            afterMs={STUCK_HELP_MS}
            // The screen's own message, not just the "not connected yet"
            // detail: when the check itself is erroring, `waitingDetail`
            // already carries that text, and the assistant should see
            // exactly what the resident is reading, not a blank string.
            lastError={waitingDetail ?? undefined}
            // A real query failure (the app can't even reach our own
            // backend) is a stronger signal than "the connector hasn't
            // booted yet" (a 200 with connected: false, true for the whole
            // ordinary wait). Only the former skips the idle timer.
            hasFailed={Boolean(windowsStatus.error)}
            seed={`The app can't reach my ${CONNECTOR}. Can you help me work out why?`}
            label="Still checking? Get help"
          />
        </div>
      </div>
    );
  }

  // ── 10b · the KLR 200's five taps into sending mode ──────────────────────
  //
  // One illustrated step per pad screen, in order, so the resident can match
  // the pad in their hand against the picture at every tap — the same
  // navigate-by-picture rule as the rest of the module, compressed onto one
  // scrollable screen because these five taps are one uninterrupted menu walk
  // with no waits between them.
  if (stage === "klg_send_mode") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("klg_setup")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">Put your {TOUCH_REMOTE} in sending mode</h1>
            <p className="text-muted text-[17px] leading-6">
              Your VELUX Touch offers its window keys from its &quot;Send copy&quot; screen. Five
              taps get you there.
            </p>
          </div>
          <IllustratedSteps steps={KLR200_SEND_MODE_STEPS} />
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton
            label="I've tapped the forward arrow"
            onClick={() => setStage("klg_transfer")}
          />
        </div>
      </div>
    );
  }

  // ── 11 · the key transfer: two presses inside one two-minute window ──────
  if (stage === "klg_transfer") {
    // A KLR 200 arrives here already in sending mode (the previous stage);
    // the KLR 300 enters sending mode right here, with the illustrated press.
    const klr200 = ownTouch && ownModel === "KLR 200";
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage(klr200 ? "klg_send_mode" : "klg_setup")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          {/* The KLR 300 steps carry their own pictures, so this hero only
              earns its place once the demo loop exists; the KLR 200 variant
              shows the connector, its one remaining press. */}
          {(klr200 || LOOPS.keyTransfer) && (
            <div
              className={`flex h-[220px] w-full shrink-0 items-center justify-center gap-2 overflow-hidden rounded-2xl px-2 ${MODULE_HERO}`}
            >
              {!klr200 && (
                <DemoLoop
                  src={touchLoop(LOOPS.keyTransfer)}
                  fallback={touchImage("sendkey")}
                  alt="Pressing the key button on the VELUX Touch until SEND KEY appears, then pressing the connector's key button with the pin"
                  className="h-[165px] w-auto"
                />
              )}
              {klr200 && (
                <img
                  src="/brand/onboarding/windows-connector-top.png"
                  alt={`The ${CONNECTOR} with its key symbol button`}
                  className="h-[150px] w-auto"
                />
              )}
            </div>
          )}
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-headline">
              Linking your VELUX remotes to the {CONNECTOR}
            </h1>
            <p className="text-muted text-[17px] leading-6">
              {klr200
                ? `Your ${TOUCH_REMOTE} is offering the keys now, and only for about two minutes. Press the connector right away.`
                : `Two presses, one after the other. Keep them close together, because the ${TOUCH_REMOTE} only offers the keys for about two minutes.`}
            </p>
          </div>
          {/* Every step is an action followed by the thing you see when it
              worked. The flashing green light is the connector answering the
              pin press, so it belongs to that step. Stranded at the head of the
              next one it reads as a fifth thing to do, and someone who does not
              see it has already moved on. */}
          {klr200 ? (
            <NumberedSteps
              steps={[
                `On the connector, press the key button with the pin. Less than one second. A green light flashes slowly.`,
                "Then we scan for your VELUX products and show you what we find.",
              ]}
            />
          ) : (
            <IllustratedSteps steps={KLR300_KEY_TRANSFER_STEPS} />
          )}
          {klr200 ? (
            <HelpHint
              question="Nothing happening after two minutes?"
              answer={`Put your ${TOUCH_REMOTE} back in sending mode (the previous screen shows the taps), then press the ${CONNECTOR}'s key button right after.`}
            />
          ) : (
            <>
              <HelpHint
                question='Showing "RECEIVE KEY" instead?'
                answer={`The button was pressed twice. Wait for the ${TOUCH_REMOTE} to return to its normal screen, then press the key button once, firmly, until "SEND KEY" shows.`}
              />
              <HelpHint
                question='Still showing "SEND KEY" after two minutes?'
                answer={`Press the ${CONNECTOR}'s key button again, then press the ${TOUCH_REMOTE}'s key button firmly until "SEND KEY" shows once more.`}
              />
            </>
          )}
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton
            label={klr200 ? "The green light flashed" : '"SEND KEY" is gone'}
            onClick={startDiscovery}
          />
        </div>
      </div>
    );
  }

  // ── 12 · list the actuators the connector actually exposes ───────────────
  if (stage === "discovering") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("klg_transfer")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-6 px-7 pt-6 pb-8">
          <div className="flex items-center justify-center pt-2">
            <div className="bg-sky-subtle flex size-[140px] items-center justify-center rounded-full">
              {covers.length === 0 ? (
                <Loader2 className="text-sky size-12 animate-spin" strokeWidth={2.5} />
              ) : (
                <Check className="text-success size-12" strokeWidth={2.5} />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 text-center">
            <h1 className="text-primary type-headline">
              {covers.length === 0
                ? "Finding your VELUX windows…"
                : `Found ${spellCountLower(covers.length)} of your VELUX products`}
            </h1>
            <p className="text-muted text-[15px] leading-[22px]">
              {covers.length === 0
                ? reloadAttempts >= MAX_AUTO_RELOADS && !rescan.isPending
                  ? `The ${PRODUCT_NAMES.hub} still can't see any windows. Check the key transfer finished on the ${TOUCH_REMOTE}, then rescan.`
                  : `The connector is introducing each window to the ${PRODUCT_NAMES.hub}.`
                : describeDiscovery(covers.length, windowCount, blindCount)}
            </p>
          </div>
          <div className="flex flex-col items-center gap-2">
            {covers.map((cover) => (
              <div
                key={cover.entity_id}
                className="border-border bg-background flex w-fit items-center gap-2.5 rounded-sm border py-2.5 pr-6 pl-3"
              >
                <div className="bg-success flex size-7 shrink-0 items-center justify-center rounded-full">
                  <Check className="size-3.5 text-white" strokeWidth={3} />
                </div>
                <div className="flex flex-col gap-0.5">
                  <p className="text-primary text-[13px] leading-[18px]">
                    {labelForCover(cover.device_class, blindCount)}
                  </p>
                  <p className="text-muted text-xs leading-4">{cover.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
          {rescan.isError && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {getApiErrorMessage(rescan.error, "Could not rescan the connector.")}
            </p>
          )}
          <PillButton
            label="Continue"
            disabled={covers.length === 0}
            onClick={() => setStage("safety")}
          />
          <PillButton
            label={covers.length === 0 ? "Look again" : "Rescan for a missing window"}
            variant="ghost"
            busy={rescan.isPending}
            onClick={() => {
              setReloadAttempts(0);
              rescan.mutate();
            }}
          />
        </div>
      </div>
    );
  }

  // ── 13 · safety primer before opening windows ────────────────────────────
  if (stage === "safety") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("discovering")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-4">
          <div className={`h-[173px] w-full shrink-0 overflow-hidden rounded-2xl ${MODULE_HERO}`}>
            <img
              src="/brand/onboarding/windows-copy-photo.jpg"
              alt="A finger pressing a VELUX wall remote"
              className="size-full object-cover"
            />
          </div>
          <h1 className="text-primary type-headline">
            Your home is about to move on its own, for the first time
          </h1>
          {/* Two facts the resident just established, as check rows like the
              connect steps elsewhere in the flow. Green here means "working",
              so it stays on the icon; a solid green bar reads as an alert. */}
          <div className="flex flex-col gap-2">
            <p className="type-body-sm text-primary flex items-center gap-2">
              <Check className="text-success size-5 shrink-0" strokeWidth={3} aria-hidden />
              {spellCount(covers.length)} VELUX {covers.length === 1 ? "product" : "products"}{" "}
              connected
            </p>
            {scanTotal > 0 && (
              <p className="type-body-sm text-primary flex items-center gap-2">
                {covers.length === scanTotal ? (
                  <Check className="text-success size-5 shrink-0" strokeWidth={3} aria-hidden />
                ) : (
                  <TriangleAlert className="text-warning size-5 shrink-0" aria-hidden />
                )}
                Your scan lists {describeScan(windowCount, blindCount)}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-muted text-[17px] leading-6">
              Now we&apos;ll find out where each one is. We&apos;ll open them one at a time, and you
              tell us which room moved.
            </p>
            <p className="text-muted text-[17px] leading-6">
              Before we start, make sure it&apos;s safe. No one leaning out, and it isn&apos;t
              raining.
            </p>
          </div>
        </div>
        <div className="px-6 pt-4 pb-7">
          <PillButton label="Start mapping" onClick={startMapping} />
        </div>
      </div>
    );
  }

  // ── 14 · per window — open it, ask which moved, confirm, repeat ──────────
  if (stage === "identify") {
    const lastMapped = windows.find((w) => w.id === matched[matched.length - 1]);
    const allMapped = loopDone;
    // Progress is always phrased as a count, never as "Window 2 of 8": a digit
    // beside "window" reads as which window, and the flow numbers nothing.
    const progress = describeMappingProgress(pairs.length, blinds.length, totalToMap);
    const windowList = (
      <div className="flex flex-col gap-3">
        {pickable.map((w) =>
          matched.includes(w.id) ? (
            <div
              key={w.id}
              className="border-success flex items-center gap-3 rounded-2xl border-2 bg-white p-4"
            >
              <div className="bg-success flex size-6 shrink-0 items-center justify-center rounded-full">
                <Check className="size-3.5 text-white" strokeWidth={3} />
              </div>
              <span className="text-primary flex-1 text-[17px] leading-6">{displayName(w)}</span>
              <span className="text-success type-caption">Mapped</span>
            </div>
          ) : (
            <button
              key={w.id}
              type="button"
              onClick={() => pickWindow(w.id)}
              disabled={mapPhase !== "pick" || reopening}
              className="border-border hover:border-sky flex items-center rounded-2xl border-2 bg-white p-4 text-left transition-colors disabled:opacity-60"
            >
              <span className="text-primary flex-1 text-[17px] leading-6">{w.label}</span>
            </button>
          ),
        )}
        {/* The list is deliberately short. This is the way out for a house
            whose scan never recorded which windows are motorised. */}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAllWindows(true)}
            className="text-muted hover:text-primary type-caption self-start underline transition-colors"
          >
            I don&apos;t see it in this list
          </button>
        )}
      </div>
    );

    // Error state: the user watched and nothing moved. We retry the same
    // window — matched is untouched, so "opening" re-sends the same command.
    if (mapPhase === "nothing_moved") {
      return (
        <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
          <AppBar
            stepKey="windows"
            stage={stage}
            title={PRODUCT_NAMES.windows}
            onBack={() => setMapPhase("pick")}
          />
          <WindowsProgress stage={stage} />
          <div className="flex flex-1 flex-col gap-6 px-7 pt-4">
            <div className="flex items-center justify-center pt-2">
              <div className="bg-surface-subtle flex size-[140px] items-center justify-center rounded-full">
                <TriangleAlert className="text-accent size-12" strokeWidth={2} />
              </div>
            </div>
            <div className="flex flex-col gap-2 text-center">
              <h1 className="text-primary type-headline">Nothing moved?</h1>
              <p className="text-muted text-[15px] leading-[22px]">
                {driveError ||
                  "No harm done. Sometimes a window doesn't answer the first time. We'll send the command again. Keep an eye on your windows."}
              </p>
            </div>
            <HelpHint
              question="Still nothing after a few tries?"
              answer={`Check the ${CONNECTOR}'s power light is solid white and that it's still connected to your router. A window can also be out of radio reach, so try again standing closer to it.`}
            />
          </div>
          <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
            <PillButton label="Try opening it again" onClick={() => setMapPhase("opening")} />
            <StuckHelp
              stepKey="windows"
              stage="identify_nothing_moved"
              lastError={
                driveError ||
                `We opened a product through ${CONNECTOR} but the user saw no window move. ${pairs.length} of ${totalToMap} products are mapped so far, ${blinds.length} set aside as blinds.`
              }
              seed="I'm mapping windows and none of them moved when the app opened one. Can you check what's wrong?"
              label="Ask why nothing moved"
            />
            <PillButton
              label="It did move. Let me pick it"
              variant="ghost"
              onClick={() => setMapPhase("pick")}
            />
          </div>
        </div>
      );
    }

    // The product that moved was a blind. Same beat as a pick, different words:
    // nothing was mapped, nothing is wrong, the loop simply moves on.
    if (mapPhase === "blind") {
      return (
        <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
          <AppBar stepKey="windows" stage={stage} title={PRODUCT_NAMES.windows} />
          <WindowsProgress stage={stage} />
          <div className="flex flex-1 flex-col gap-6 px-7 pt-4">
            <div className="flex flex-col gap-2">
              <p className="text-muted type-label">{progress}</p>
              <h1 className="text-primary type-headline">That blind is set aside</h1>
              <p className="text-muted text-[17px] leading-6">
                We&apos;re closing it again. It keeps working from its own remote, and VELUX will
                connect it later.{" "}
                {allMapped ? "That was the last product." : "Next, we open the next product."}
              </p>
            </div>
            {windowList}
          </div>
          <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
            <PillButton
              label={allMapped ? "All windows mapped" : "Open the next one"}
              onClick={openNextWindow}
            />
          </div>
        </div>
      );
    }
    // Confirmation beat after each pick — nothing moves until the user says
    // they're ready, since they may need to walk to another room first.
    if (mapPhase === "mapped") {
      return (
        <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
          <AppBar stepKey="windows" stage={stage} title={PRODUCT_NAMES.windows} />
          <WindowsProgress stage={stage} />
          <div className="flex flex-1 flex-col gap-6 px-7 pt-4">
            <div className="flex flex-col gap-2">
              <p className="text-muted type-label">{progress}</p>
              <h1 className="text-primary type-headline">{lastMapped?.label} is mapped</h1>
              <p className="text-muted text-[17px] leading-6">
                We&apos;re closing that window again.{" "}
                {allMapped
                  ? "That was the last one."
                  : "When you can see your windows, open the next one."}
              </p>
            </div>
            {windowList}
          </div>
          <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
            <PillButton
              label={allMapped ? "All windows mapped" : "Open the next window"}
              onClick={openNextWindow}
            />
            {/* The moment a wrong pick is noticed is right here, while the
                resident is still looking at the window that moved. */}
            {lastMapped && (
              <PillButton
                label="That wasn't it, try again"
                variant="ghost"
                onClick={() => remapWindow(lastMapped.id)}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="windows"
          stage={stage}
          title={PRODUCT_NAMES.windows}
          onBack={() => setStage("safety")}
        />
        <WindowsProgress stage={stage} />
        <div className="flex flex-1 flex-col gap-6 px-7 pt-4">
          {mapPhase === "opening" ? (
            <>
              <div className="flex items-center justify-center pt-2">
                <div className="bg-sky-subtle flex size-[140px] items-center justify-center rounded-full">
                  <Loader2 className="text-sky size-12 animate-spin" strokeWidth={2.5} />
                </div>
              </div>
              <div className="flex flex-col gap-2 text-center">
                <h1 className="text-primary type-headline">Opening a window…</h1>
                <p className="text-muted text-[15px] leading-[22px]">
                  Keep an eye on your windows. Which one moves?
                </p>
                <p className="text-muted type-label">{progress} so far</p>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <p className="text-muted type-label">{progress}</p>
                <h1 className="text-primary type-headline">Which window just moved?</h1>
                <p className="text-muted text-[17px] leading-6">
                  Pick the one that opened. We&apos;ll map it to that room.
                </p>
              </div>
              {windowList}
              {driveError && (
                <p role="alert" className="text-destructive text-center text-[15px]">
                  {driveError}
                </p>
              )}
              {/* Everything that could change the turn is held until the
                  window has finished being shown again. Answering "which one
                  moved?" while it is still moving would land a pick's close
                  and this sequence's open on the same actuator out of order,
                  leaving a mapped window open while the loop moves on to the
                  next one — two windows ajar, and no way to tell them apart. */}
              <PillButton
                label="Open it again"
                variant="ghost"
                busy={reopening}
                busyLabel="Opening it again…"
                onClick={() => void reopenCurrent()}
              />
              {/* The answer a house with electric blinds needs. Without it the
                  list runs out of windows while the connector still has
                  products to open, and the step has no way to finish. */}
              <PillButton
                label="That was a blind, not a window"
                variant="ghost"
                disabled={reopening}
                onClick={setAsideBlind}
              />
              <PillButton
                label="No window moved"
                variant="ghost"
                disabled={reopening}
                onClick={() => setMapPhase("nothing_moved")}
              />
            </>
          )}
        </div>
      </div>
    );
  }

  // ── 15 · all windows mapped ──────────────────────────────────────────────
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
      <AppBar stepKey="windows" stage={stage} title={PRODUCT_NAMES.windows} />
      <WindowsProgress stage={stage} />
      <div className="flex flex-1 flex-col gap-5 px-7 pt-6">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-primary type-headline">Your windows are connected</h1>
          <p className="text-muted text-[17px] leading-6">
            {pairs.length > 0
              ? `All ${spellCountLower(pairs.length)} ${
                  pairs.length === 1 ? "window is" : "windows are"
                } mapped to their rooms. `
              : "We couldn't map any windows this time. "}
            SmartClimate opens a window when a room needs fresh air, and closes it again when the
            air is fresh.
          </p>
          {blinds.length > 0 && (
            <p className="text-muted text-[17px] leading-6">
              {describeBlindsSetAside(blinds.length)}
            </p>
          )}
        </div>
        {/* Every row used to carry the same green check and nothing else, so a
            wrong pick was unverifiable and two windows in one room were
            indistinguishable. Moving a window is the proof; naming it is how
            the resident keeps the answer. */}
        <div className="flex flex-col gap-2">
          {windows
            .filter((w) => matched.includes(w.id))
            .map((w) => (
              <div key={w.id} className="bg-background flex flex-col gap-2 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="bg-success flex size-9 shrink-0 items-center justify-center rounded-full">
                    <Check className="size-4.5 text-white" strokeWidth={3} />
                  </div>
                  {editingId === w.id ? (
                    <input
                      // The row turned into a field because the resident tapped
                      // Rename; landing the cursor anywhere else would be wrong.
                      autoFocus
                      type="text"
                      value={customNames[w.id] ?? w.label}
                      aria-label={`Name for ${w.label}`}
                      maxLength={40}
                      onChange={(e) =>
                        setCustomNames((prev) => ({ ...prev, [w.id]: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "Escape") {
                          setEditingId(null);
                        }
                      }}
                      onBlur={() => setEditingId(null)}
                      className="border-sky text-primary min-w-0 flex-1 rounded-xl border-2 bg-white px-3 py-1.5 text-[17px] leading-6 outline-none"
                    />
                  ) : (
                    <p className="text-primary min-w-0 flex-1 text-[17px] leading-6">
                      {displayName(w)}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 pl-12">
                  {/* Nothing drives a motor while the mapping is being
                      applied: the rename lands in Home Assistant mid-flight,
                      so a command issued before it would address an entity id
                      that no longer exists. */}
                  <button
                    type="button"
                    onClick={() => void verifyWindow(w.id)}
                    disabled={verifyingId !== null || applyMapping.isPending}
                    className="text-sky type-caption underline disabled:opacity-60"
                  >
                    {verifyingId === w.id ? "Moving it…" : "Move it"}
                  </button>
                  {/* Both are held while a window is being moved: renaming or
                      re-queueing an actuator mid-travel would act on hardware
                      that is still carrying out the last instruction. */}
                  <button
                    type="button"
                    onClick={() => setEditingId(w.id)}
                    disabled={verifyingId !== null || applyMapping.isPending}
                    className="text-muted hover:text-primary type-caption underline transition-colors disabled:opacity-60"
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => remapWindow(w.id)}
                    disabled={verifyingId !== null || applyMapping.isPending}
                    className="text-muted hover:text-primary type-caption underline transition-colors disabled:opacity-60"
                  >
                    Not this one
                  </button>
                </div>
              </div>
            ))}
        </div>
        {driveError && (
          <p role="alert" className="text-destructive text-center text-[15px]">
            {driveError}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
        {(applyError || error) && (
          <p role="alert" className="text-destructive text-center text-[15px]">
            {applyError || error}
          </p>
        )}
        {/* And the mapping is never applied while a motor is moving. Applying
            renames the entity, so the verify's trailing close would address an
            id that has gone — leaving that window open in a house nobody is
            looking at any more, having just told the resident we closed it. */}
        <PillButton
          label="See what's next"
          busy={busy || applyMapping.isPending}
          disabled={verifyingId !== null}
          onClick={() => void finish()}
        />
        {applyError && onSkip && (
          <SkipFailedStep busy={skipping} onSkip={() => onSkip(applyError)} />
        )}
      </div>
    </div>
  );
}
